import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

// Small dependency-free charts for the dashboard. Colours come from the theme
// tokens, so they work in light and dark mode.

export interface StackedSeries {
  key: string;
  label: string;
  /** Tailwind bg class for the bar segment and legend swatch. */
  className: string;
}

/** Vertical stacked bars, one per category (e.g. month). Hover shows the breakdown. */
export function StackedBarChart({
  data,
  series,
  height = 160,
  formatLabel = (l) => l,
  onSegmentClick,
}: {
  data: { label: string; values: Record<string, number> }[];
  series: StackedSeries[];
  height?: number;
  formatLabel?: (label: string) => string;
  /** Makes each segment clickable (drill-down); called with the bar's label and series key. */
  onSegmentClick?: (label: string, seriesKey: string) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const totals = data.map((d) => series.reduce((s, x) => s + (d.values[x.key] || 0), 0));
  const max = Math.max(1, ...totals);
  // A few horizontal gridlines at "nice" values.
  const step = niceStep(max / 3);
  const ticks = Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step).filter((t) => t > 0);

  return (
    <div>
      <div className="relative ml-7" style={{ height }}>
        {ticks.map((t) => (
          <div key={t} className="absolute inset-x-0 border-t border-dashed border-slate-200" style={{ bottom: `${(t / max) * 100}%` }}>
            <span className="absolute -top-2 right-full mr-1.5 text-[10px] tabular-nums text-slate-400">{t}</span>
          </div>
        ))}
        <div className="absolute inset-0 flex items-end gap-1.5">
          {data.map((d, i) => (
            <div
              key={d.label}
              className="relative flex h-full flex-1 flex-col justify-end"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              <div className={`flex w-full flex-col-reverse overflow-hidden rounded-t-md transition-opacity ${hover !== null && hover !== i ? 'opacity-50' : ''}`}
                style={{ height: `${(totals[i] / max) * 100}%`, minHeight: totals[i] ? 2 : 0 }}>
                {series.map((s) => {
                  const v = d.values[s.key] || 0;
                  if (!v) return null;
                  const style = { height: `${(v / totals[i]) * 100}%` };
                  return onSegmentClick
                    ? <button key={s.key} type="button" aria-label={`${formatLabel(d.label)} · ${s.label}: ${v} — show domains`}
                        className={`${s.className} block w-full cursor-pointer transition-[filter] hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400`}
                        style={style} onClick={() => onSegmentClick(d.label, s.key)} />
                    : <div key={s.key} className={s.className} style={style} />;
                })}
              </div>
              {hover === i && (
                <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-max -translate-x-1/2 rounded-md bg-ink-900 px-2.5 py-1.5 text-xs text-white shadow-lg">
                  <div className="font-medium">{formatLabel(d.label)}: {totals[i]}</div>
                  {series.map((s) => (
                    <div key={s.key} className="flex items-center gap-1.5 text-ink-300">
                      <span className={`h-2 w-2 rounded-sm ${s.className}`} />{s.label}: {d.values[s.key] || 0}
                    </div>
                  ))}
                  {onSegmentClick && <div className="mt-1 text-[10px] text-ink-400">Click a colour to list those domains</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="ml-7 mt-1.5 flex gap-1.5">
        {data.map((d) => (
          <div key={d.label} className="flex-1 truncate text-center text-[10px] text-slate-500">{formatLabel(d.label)}</div>
        ))}
      </div>
      <Legend series={series} />
    </div>
  );
}

/** One horizontal bar split into proportional segments, with a linked legend. */
export function SegmentBar({
  segments,
}: {
  segments: { key: string; label: string; value: number; className: string; to?: string }[];
}) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (!total) return <p className="text-sm text-slate-500">No data yet.</p>;
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
        {segments.filter((s) => s.value).map((s) => (
          <div key={s.key} className={s.className} style={{ width: `${(s.value / total) * 100}%` }} title={`${s.label}: ${s.value}`} />
        ))}
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
        {segments.map((s) => {
          const body: ReactNode = (
            <>
              <span className={`h-2.5 w-2.5 shrink-0 rounded-sm ${s.className}`} />
              <span className="flex-1 truncate text-slate-600">{s.label}</span>
              <span className="tabular-nums font-medium text-slate-900">{s.value.toLocaleString()}</span>
              <span className="w-10 text-right tabular-nums text-xs text-slate-400">{Math.round((s.value / total) * 100)}%</span>
            </>
          );
          return (
            <li key={s.key}>
              {s.to
                ? <Link to={s.to} className="-mx-1.5 flex items-center gap-2 rounded px-1.5 py-0.5 text-sm hover:bg-slate-50">{body}</Link>
                : <div className="flex items-center gap-2 text-sm">{body}</div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Line chart for a short time series (e.g. exposed domains per audit run). */
export function LineChart({
  points,
  height = 120,
  formatX,
  label,
}: {
  points: { x: string; y: number }[];
  height?: number;
  formatX: (x: string) => string;
  label: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  if (points.length === 0) return <p className="text-sm text-slate-500">No data yet.</p>;
  const W = 100; // viewBox units; the SVG scales to its container width
  const max = Math.max(1, ...points.map((p) => p.y));
  const min = Math.min(...points.map((p) => p.y));
  const lo = Math.max(0, min - (max - min) * 0.2);
  const span = Math.max(1, max - lo);
  const xs = points.map((_, i) => (points.length === 1 ? W / 2 : (i / (points.length - 1)) * W));
  const ys = points.map((p) => 100 - ((p.y - lo) / span) * 88 - 6);
  const line = xs.map((x, i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${ys[i].toFixed(2)}`).join(' ');
  const area = `${line} L${W},100 L0,100 Z`;
  const last = points[points.length - 1];

  return (
    <div>
      <div className="relative" style={{ height }} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} 100`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible">
          <path d={area} className="fill-brand-500/10" />
          <path d={line} className="fill-none stroke-brand-500" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
        </svg>
        {/* Hover targets + dots, positioned in % so they stay round. */}
        {points.map((p, i) => {
          // Hover band around each point, clipped to the chart so it never overflows.
          const x = (xs[i] / W) * 100;
          const half = 50 / points.length;
          const left = Math.max(0, x - half);
          const width = Math.min(100, x + half) - left;
          return (
          <div key={i} className="absolute top-0 h-full" style={{ left: `${left}%`, width: `${width}%` }}
            onMouseEnter={() => setHover(i)}>
            {(hover === i || i === points.length - 1) && (
              <span className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-surface bg-brand-500" style={{ left: `${((x - left) / width) * 100}%`, top: `${ys[i]}%` }} />
            )}
            {hover === i && (
              <div className={`pointer-events-none absolute bottom-full z-10 mb-1 w-max rounded-md bg-ink-900 px-2.5 py-1 text-xs text-white shadow-lg ${i === 0 ? 'left-0' : i === points.length - 1 ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}>
                {formatX(p.x)}: <span className="font-medium">{p.y}</span> {label}
              </div>
            )}
          </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-slate-500">
        <span>{formatX(points[0].x)}</span>
        <span>{formatX(last.x)}</span>
      </div>
    </div>
  );
}

function Legend({ series }: { series: StackedSeries[] }) {
  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
      {series.map((s) => (
        <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-slate-500">
          <span className={`h-2.5 w-2.5 rounded-sm ${s.className}`} />{s.label}
        </span>
      ))}
    </div>
  );
}

function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * pow;
}
